import { wrap } from "../../utils/hilma-tools";
import { ThemeProvider, Typography, type Theme } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { isMobile } from "react-device-detect";
import { useHyperTable } from "../HyperTable";
import type { HyperTableComponents } from "../types";

interface HyperTableResultCounterProps {
  theme?: Theme;
  overrideComponent?: Required<HyperTableComponents<any>>["rowCounter"];
}

export const HyperTableResultCounter: React.FC<HyperTableResultCounterProps> = (
  props,
) => {
  const { theme, overrideComponent } = props;
  const { table, pagination, findResult } = useHyperTable();

  const themeWrap = useCallback(
    theme ? wrap([ThemeProvider, { theme }]) : wrap(),
    [JSON.stringify(theme)],
  );

  const displayRange = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize + 1;
    const end = Math.min(
      start + pagination.pageSize - 1,
      findResult.totalRows ?? 0,
    );
    return {
      start: Number.isNaN(start) ? 1 : start,
      end: Number.isNaN(end) ? (findResult.totalRows ?? 0) : end,
    };
  }, [pagination.pageIndex, pagination.pageSize, findResult.totalRows]);

  if (overrideComponent) {
    const C = overrideComponent;
    return (
      <C
        currentPage={pagination.pageIndex}
        displayRange={displayRange}
        totalRows={findResult.totalRows ?? 0}
      />
    );
  }

  const { start, end } = displayRange;

  const total = Math.max(
    table.getRowCount(),
    table.getRowModel().rows.length,
  );

  if (total === 0) return null;

  return themeWrap(
    <Typography
      sx={{
        height: "80%",
        textWrap: "nowrap",
        display: "flex",
        alignItems: "baseline",
        gap: 0.25,
      }}
      fontSize="x-small"
      className="data-table-count"
    >
      מציג
      <Typography
        fontSize={isMobile ? "small" : undefined}
        component="span"
        color="primary"
      >
        {end} - {start}
      </Typography>
      מתוך
      <Typography
        fontSize={isMobile ? "small" : undefined}
        component="span"
        color="primary"
      >
        {total}
      </Typography>
      תוצאות
    </Typography>,
  );
};
